import { SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL, SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM, SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE, SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL, SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM, SDS_FONT_WEIGHT_REGULAR, SDS_FONT_FAMILY_PRIMARY } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { SDS_MOTION_DURATION_FASTEST, SDS_MOTION_EASING_LINEAR } from '@s-group/design-system-tokens/web/tokens/motion.es6.js';
import { SDS_SIZE_BORDER_RADIUS_NONE, SDS_SIZE_BORDER_RADIUS_XSMALL, SDS_SIZE_BORDER_RADIUS_XXXSMALL, SDS_SIZE_BORDER_RADIUS_XLARGE, SDS_SIZE_BORDER_RADIUS_XXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXXXLARGE, SDS_SIZE_BORDER_SMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_SMALL, SDS_SPACE_XXSMALL, SDS_SPACE_MEDIUM, SDS_SPACE_XSMALL, SDS_SPACE_LARGE, SDS_SPACE_XLARGE, SDS_SPACE_XXLARGE } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { css } from 'styled-components';
import { SDS_MISC_ALPHA_MEDIUM } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { bordered } from '../common/util.js';
import { remToPx, rem } from 'polished';
import { SDS } from '../common/namespace.js';

const BORDER_SIZE_NONE = '0';
// These values are hardcoded because they don't follow any token scale, one value would always be off
const iconSize = ({ sizing }) => sizing &&
    {
        xsmall: '14px',
        small: '16px',
        medium: '18px',
        large: '22px',
        xlarge: '28px',
    }[sizing];
const borderRadius = ({ alternative, rounding, sizing }) => rounding === 'sharp'
    ? SDS_SIZE_BORDER_RADIUS_NONE
    : alternative
        ? [
            SDS_SIZE_BORDER_RADIUS_XSMALL,
            SDS_SIZE_BORDER_RADIUS_XSMALL,
            SDS_SIZE_BORDER_RADIUS_NONE,
            SDS_SIZE_BORDER_RADIUS_NONE,
        ].join(' ')
        : rounding === 'small'
            ? SDS_SIZE_BORDER_RADIUS_XXXSMALL
            : sizing &&
                {
                    // size: border-radius
                    xsmall: SDS_SIZE_BORDER_RADIUS_XLARGE,
                    small: SDS_SIZE_BORDER_RADIUS_XXXLARGE,
                    medium: SDS_SIZE_BORDER_RADIUS_XXXXLARGE,
                    large: SDS_SIZE_BORDER_RADIUS_XXXXXLARGE,
                    xlarge: SDS_SIZE_BORDER_RADIUS_XXXXXXLARGE,
                }[sizing];
const font = ({ sizing }) => sizing &&
    [
        {
            // size: [font-size, line-height]
            xsmall: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
            small: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
            medium: [SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE],
            large: [
                SDS_FONT_SIZE_HEADING_SMALL,
                SDS_FONT_LINEHEIGHT_HEADING_SMALL,
            ],
            xlarge: [
                SDS_FONT_SIZE_HEADING_MEDIUM,
                SDS_FONT_LINEHEIGHT_HEADING_MEDIUM,
            ],
        }[sizing],
    ]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_REGULAR} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const paddingPill = ({ sizing }) => sizing &&
    {
        xsmall: [
            SDS_SPACE_XXSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }), iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_XXSMALL,
            SDS_SPACE_SMALL,
        ],
        small: [
            SDS_SPACE_XXSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }), iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_MEDIUM)).toString(),
            SDS_SPACE_XXSMALL,
            SDS_SPACE_MEDIUM,
        ],
        medium: [
            SDS_SPACE_XSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }), iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_LARGE)).toString(),
            SDS_SPACE_XSMALL,
            SDS_SPACE_LARGE,
        ],
        large: [
            SDS_SPACE_SMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }), iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_XLARGE)).toString(),
            SDS_SPACE_SMALL,
            SDS_SPACE_LARGE,
        ],
        xlarge: [
            SDS_SPACE_MEDIUM,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }), iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_XXLARGE)).toString(),
            SDS_SPACE_MEDIUM,
            SDS_SPACE_XXLARGE,
        ],
    }[sizing]
        .map(dimension => bordered(dimension))
        .join(' ');
const paddingDefault = ({ sizing }) => sizing &&
    {
        // right: space between value and icon + icon size + right spcing
        // size: [top, right, bottom, left]
        xsmall: [
            SDS_SPACE_XXSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_XXSMALL,
            SDS_SPACE_SMALL,
        ],
        small: [
            SDS_SPACE_XXSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_XXSMALL,
            SDS_SPACE_SMALL,
        ],
        medium: [
            SDS_SPACE_XSMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_XSMALL,
            SDS_SPACE_SMALL,
        ],
        large: [
            SDS_SPACE_SMALL,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_SMALL,
            SDS_SPACE_SMALL,
        ],
        xlarge: [
            SDS_SPACE_MEDIUM,
            (parseFloat(SDS_SPACE_XXSMALL) +
                parseFloat(rem(iconSize({ sizing }))) +
                parseFloat(SDS_SPACE_SMALL)).toString(),
            SDS_SPACE_MEDIUM,
            SDS_SPACE_SMALL,
        ],
    }[sizing]
        .map(dimension => bordered(dimension))
        .join(' ');
const pillIconPosition = ({ sizing }) => sizing &&
    {
        xsmall: remToPx(SDS_SPACE_SMALL),
        small: remToPx(SDS_SPACE_MEDIUM),
        medium: remToPx(SDS_SPACE_LARGE),
        large: remToPx(SDS_SPACE_LARGE),
        xlarge: remToPx(SDS_SPACE_XXLARGE),
    }[sizing];
const padding = ({ alternative, rounding, sizing }) => rounding === 'pill' && !alternative
    ? paddingPill({ sizing })
    : paddingDefault({ sizing });
const iconRightPosition = ({ alternative, sizing, rounding }) => rounding === 'pill' && !alternative
    ? pillIconPosition({ sizing })
    : remToPx(SDS_SPACE_SMALL);
const border = ({ alternative, error, theme, }) => alternative
    ? BORDER_SIZE_NONE
    : error
        ? `${SDS_SIZE_BORDER_SMALL} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_ERROR}`
        : `${SDS_SIZE_BORDER_SMALL} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_DEFAULT_NEUTRAL}`;
const alternativeBorder = ({ alternative, error, theme, }) => alternative && !error
    ? `${SDS_SIZE_BORDER_SMALL} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_DEFAULT_NEUTRAL}`
    : alternative && error
        ? `${SDS_SIZE_BORDER_SMALL} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_ERROR}`
        : undefined; // Return undefined so the value won't break regular borders
const backgroundColor = ({ alternative, theme }) => alternative
    ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_NEUTRAL
    : theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_DEFAULT_NEUTRAL;
const outline = ({ alternative, theme }) => alternative
    ? 'none'
    : `${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_FOCUS}
    solid ${SDS_SIZE_BORDER_SMALL}`;
const boxShadow = ({ alternative, theme }) => alternative &&
    `0px ${SDS_SIZE_BORDER_SMALL} 0px 0px ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_FOCUS}`;
const textColor = ({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL;
// Svg fill as background cant handle hex colors with # in them
const iconColor = ({ theme }) => typeof theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_NEUTRAL ===
    'string'
    ? theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_NEUTRAL.replace(/[^A-Fa-f0-9]/g, '')
    : theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_NEUTRAL;
const SelectStyles = css `
    border: ${border};
    border-bottom: ${alternativeBorder};
    border-radius: ${borderRadius};
    color: ${textColor};
    cursor: pointer;
    font: ${font};
    padding: ${padding};
    transition-duration: ${SDS_MOTION_DURATION_FASTEST};
    transition-property: border-color, box-shadow, outline;
    transition-timing-function: cubic-bezier(${SDS_MOTION_EASING_LINEAR});
    -webkit-appearance: none;
    -moz-appearance: none;

    background: ${backgroundColor}
        url("data:image/svg+xml,<svg width='${iconSize}' height='${iconSize}' fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill-rule='evenodd' d='m12 14.94 6.97-6.97 1.06 1.06L12 17.062l-8.03-8.03L5.03 7.97 12 14.94Z' fill='%23${iconColor}'/></svg>")
        no-repeat;
    background-position: right ${iconRightPosition} top 50%;

    &:focus-visible {
        border-color: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_FOCUS};
        box-shadow: ${boxShadow};
        outline: ${outline};
    }

    &[aria-disabled='true'],
    &[disabled] {
        opacity: ${SDS_MISC_ALPHA_MEDIUM};
        pointer-events: none;
    }

    &:invalid {
        border-color: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_ERROR};
    }
`;

export { SelectStyles };
//# sourceMappingURL=Select.styles.js.map
